import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/UI/Button/Button';
import axiosInstance, { BASE_API_URL } from '../../../utils/router/axiosInstance';
import { getCalibrationViewport } from '../../../utils/mapCoordinates';
import {
  clearMapCalibration,
  getMapCalibration,
  isMapCalibrationLoaded,
  loadMapCalibration,
  saveMapCalibration,
} from '../../../utils/mapCalibration';
import './mapCalibrationPanel.css';

const buildFormState = (calibration) => ({
  planWidth: calibration.planWidth ? String(calibration.planWidth) : '',
  planHeight: calibration.planHeight ? String(calibration.planHeight) : '',
  offsetLeftPercent: String(calibration.offsetLeftPercent ?? 0),
  offsetTopPercent: String(calibration.offsetTopPercent ?? 0),
  activeWidthPercent: String(calibration.activeWidthPercent ?? 100),
  activeHeightPercent: String(calibration.activeHeightPercent ?? 100),
  invertY: Boolean(calibration.invertY),
});

const parseDecimalInput = (value) => value.replace(',', '.').replace(/[^0-9.-]+/g, '');

const MapCalibrationPanel = ({ selectedBuildingId, selectedFloor }) => {
  const [mapImage, setMapImage] = useState('');
  const [backendSize, setBackendSize] = useState({ x: 0, y: 0 });
  const [formState, setFormState] = useState(buildFormState(getMapCalibration({})));
  const [status, setStatus] = useState('');
  // WEB-4 (ревью Codex ×2/№3, High): ключ `${b}:${f}`, для которого форма
  // РЕАЛЬНО заполнена завершившейся загрузкой. Право сохранения — производное
  // от него (см. canPersist ниже), а не отдельный stale-able флаг: компонент
  // не ремоунтится при смене этажа (wifi.jsx без key), и state-флаг ещё кадр
  // жил бы со значением СТАРОГО этажа — save записал бы старую форму в новый.
  const [loadedFormKey, setLoadedFormKey] = useState(null);

  useEffect(() => {
    if (!selectedBuildingId || !selectedFloor) {
      setMapImage('');
      setBackendSize({ x: 0, y: 0 });
      setFormState(buildFormState(getMapCalibration({})));
      setStatus('');
      setLoadedFormKey(null);
      return;
    }

    let isMounted = true;

    const fetchFloorData = async () => {
      try {
        const response = await axiosInstance.get(`website/map/${selectedBuildingId}?floor=${selectedFloor}`);
        const floorData = response.data?.[0];
        const fallbackPlanWidth = Number(floorData?.floor_size?.x) || 0;
        const fallbackPlanHeight = Number(floorData?.floor_size?.y) || 0;
        // WEB-4: awaitable-загрузка — форма заполняется значением с сервера,
        // а не гонкой с фоновым GET (при выключенном флаге = легаси-чтение).
        const calibration = await loadMapCalibration({
          buildingId: selectedBuildingId,
          floor: selectedFloor,
          fallbackPlanWidth,
          fallbackPlanHeight,
        });

        const calibrationLoaded = isMapCalibrationLoaded({
          buildingId: selectedBuildingId,
          floor: selectedFloor,
        });

        if (!isMounted) {
          return;
        }

        setMapImage(floorData?.floor_plan && floorData.floor_plan !== '404' ? floorData.floor_plan : '');
        setBackendSize({ x: fallbackPlanWidth, y: fallbackPlanHeight });
        setFormState(buildFormState(calibration));
        setLoadedFormKey(`${selectedBuildingId}:${selectedFloor}`);
        setStatus(calibrationLoaded
          ? ''
          : 'Не удалось загрузить калибровку с сервера — сохранение отключено. Выберите этаж заново или обновите страницу.');
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setMapImage('');
        setBackendSize({ x: 0, y: 0 });
        setLoadedFormKey(null);
        setStatus('Не удалось загрузить карту этажа для калибровки.');
      }
    };

    fetchFloorData();

    return () => {
      isMounted = false;
    };
  }, [selectedBuildingId, selectedFloor]);

  const calibration = useMemo(
    () => ({
      planWidth: Number(formState.planWidth),
      planHeight: Number(formState.planHeight),
      offsetLeftPercent: Number(formState.offsetLeftPercent),
      offsetTopPercent: Number(formState.offsetTopPercent),
      activeWidthPercent: Number(formState.activeWidthPercent),
      activeHeightPercent: Number(formState.activeHeightPercent),
      invertY: formState.invertY,
    }),
    [formState],
  );

  const previewViewport = getCalibrationViewport({
    mapWidth: 100,
    mapHeight: 100,
    calibration,
  });

  // Право сохранения — производное на КАЖДЫЙ рендер, stale-окна нет: форма
  // принадлежит текущему выбору (иначе при смене этажа save записал бы форму
  // старого этажа в новый) И сервер успешно отвечал за этот этаж (иначе PUT
  // формы-фолбэка затёр бы серверную калибровку при временно упавшем GET;
  // в легаси-режиме isMapCalibrationLoaded всегда true).
  const selectionKey =
    selectedBuildingId && selectedFloor ? `${selectedBuildingId}:${selectedFloor}` : null;
  const canPersist =
    Boolean(selectionKey) &&
    loadedFormKey === selectionKey &&
    isMapCalibrationLoaded({ buildingId: selectedBuildingId, floor: selectedFloor });

  // Ревью-воркфлоу (Medium): поздний ответ save/reset, стартовавших на СТАРОМ
  // этаже, не должен трогать форму/статус НОВОГО (setFormState после await
  // работает по closure-значениям момента клика). Ref всегда несёт актуальный
  // ключ выбора — континуации сверяются с ним и молча выходят при смене.
  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;

  const updateField = (fieldName) => (event) => {
    const nextValue = fieldName === 'invertY' ? event.target.checked : parseDecimalInput(event.target.value);

    setFormState((previousState) => ({
      ...previousState,
      [fieldName]: nextValue,
    }));
    setStatus('');
  };

  const handleSave = async () => {
    // Страховка поверх disabled-кнопки: PUT незагруженной формы = затирание
    // серверной калибровки fallback-значениями.
    if (!canPersist) {
      setStatus('Калибровка ещё не загружена с сервера — сохранение отключено.');
      return;
    }

    // WEB-4: в API-режиме save возвращает Promise (PUT на сервер), в легаси —
    // значение; Promise.resolve выравнивает оба случая.
    const requestKey = selectionKey; // фиксируем ДО await — ключ момента клика
    try {
      const savedCalibration = await Promise.resolve(
        saveMapCalibration({
          buildingId: selectedBuildingId,
          floor: selectedFloor,
          calibration,
          fallbackPlanWidth: backendSize.x,
          fallbackPlanHeight: backendSize.y,
        }),
      );

      if (selectionKeyRef.current !== requestKey) {
        return; // поздний ответ: выбор сменился, форму нового этажа не трогаем
      }

      setFormState(buildFormState(savedCalibration));
      setStatus('Калибровка сохранена.');
    } catch (error) {
      if (selectionKeyRef.current !== requestKey) {
        return;
      }
      setStatus('Не удалось сохранить калибровку на сервере.');
    }
  };

  const handleReset = async () => {
    if (!canPersist) {
      setStatus('Калибровка ещё не загружена с сервера — сброс отключён.');
      return;
    }

    const requestKey = selectionKey; // фиксируем ДО await — ключ момента клика
    try {
      await Promise.resolve(
        clearMapCalibration({
          buildingId: selectedBuildingId,
          floor: selectedFloor,
        }),
      );

      if (selectionKeyRef.current !== requestKey) {
        return; // поздний ответ: выбор сменился, форму нового этажа не трогаем
      }

      setFormState(
        buildFormState(
          getMapCalibration({
            buildingId: selectedBuildingId,
            floor: selectedFloor,
            fallbackPlanWidth: backendSize.x,
            fallbackPlanHeight: backendSize.y,
          }),
        ),
      );
      setStatus('Калибровка сброшена к данным backend.');
    } catch (error) {
      if (selectionKeyRef.current !== requestKey) {
        return;
      }
      setStatus('Не удалось сбросить калибровку на сервере.');
    }
  };

  return (
    <div className="mapCalibrationPanel">
      <div className="mapCalibrationHeader">
        <h3>Калибровка плана</h3>
        <p>Задай реальные размеры этажа и рабочую область внутри картинки, где лежит сам план без полей.</p>
      </div>

      <div className="mapCalibrationMeta">
        <span>{`Этаж: ${selectedFloor || '-'}`}</span>
        <span>{`Backend floor_size: ${backendSize.x || '-'} x ${backendSize.y || '-'}`}</span>
      </div>

      <div className="mapCalibrationGrid">
        <label>
          Ширина плана, м
          <input value={formState.planWidth} onChange={updateField('planWidth')} placeholder="15" />
        </label>
        <label>
          Высота плана, м
          <input value={formState.planHeight} onChange={updateField('planHeight')} placeholder="14.7" />
        </label>
        <label>
          Отступ слева, %
          <input value={formState.offsetLeftPercent} onChange={updateField('offsetLeftPercent')} placeholder="10" />
        </label>
        <label>
          Отступ сверху, %
          <input value={formState.offsetTopPercent} onChange={updateField('offsetTopPercent')} placeholder="6" />
        </label>
        <label>
          Ширина рабочей области, %
          <input value={formState.activeWidthPercent} onChange={updateField('activeWidthPercent')} placeholder="78" />
        </label>
        <label>
          Высота рабочей области, %
          <input value={formState.activeHeightPercent} onChange={updateField('activeHeightPercent')} placeholder="76" />
        </label>
      </div>

      <label className="mapCalibrationCheckbox">
        <input type="checkbox" checked={formState.invertY} onChange={updateField('invertY')} />
        Инвертировать ось Y
      </label>

      <div className="mapCalibrationActions">
        <Button className={'floorMap'} text="Сохранить калибровку" onClick={handleSave} disabled={!canPersist} />
        <Button className={'floorMap'} text="Сбросить" onClick={handleReset} disabled={!canPersist} />
      </div>

      {status && <p className="mapCalibrationStatus">{status}</p>}

      <p className="mapCalibrationHint">
        Если размеры на чертеже указаны в миллиметрах, переводи их в метры. Например, 15000 мм = 15 м.
      </p>

      {mapImage && (
        <div className="mapCalibrationPreview">
          <img src={`${BASE_API_URL}${mapImage}`} className="mapCalibrationPreviewImage" alt="preview floor plan" />
          <div
            className="mapCalibrationBounds"
            style={{
              left: `${previewViewport.left}%`,
              top: `${previewViewport.top}%`,
              width: `${previewViewport.width}%`,
              height: `${previewViewport.height}%`,
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default MapCalibrationPanel;
